import React from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Eye, ArrowRight } from 'lucide-react';

const categoryColors = {
  government: 'badge-government',
  phenomena: 'badge-phenomena',
  consciousness: 'badge-consciousness',
  physics: 'badge-physics',
  witnesses: 'badge-witnesses',
  creative: 'badge-creative'
};

export default function StoryCard({ story, size = 'normal' }) {
  const isHero = size === 'hero';
  const isCompact = size === 'compact';

  return (
    <Link
      to={`/story/${story.story_id}`}
      className={`card-dark block overflow-hidden group ${
        isHero ? 'col-span-full' : isCompact ? '' : 'col-span-full md:col-span-4'
      }`}
      data-testid={`story-card-${story.story_id}`}
    >
      {/* Image */}
      {story.image_url && (
        <div className={`relative overflow-hidden ${isHero ? 'h-64 md:h-96' : 'h-48'}`}>
          <img
            src={story.image_url}
            alt={story.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Category badge */}
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider rounded-sm ${categoryColors[story.category] || 'badge-phenomena'}`}>
              {story.category}
            </span>
          </div>

          {/* Awe moment indicator */}
          {story.is_awe_moment && (
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-sm">
                Awe Moment
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className={`p-6 ${isHero ? 'md:p-8' : ''}`}>
        <h3 className={`font-heading font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors ${
          isHero ? 'text-2xl md:text-4xl' : isCompact ? 'text-lg' : 'text-xl'
        }`}>
          {story.title}
        </h3>
        
        {story.subtitle && !isCompact && (
          <p className="text-white/60 text-sm mb-3">{story.subtitle}</p>
        )}
        
        <p className={`text-white/50 leading-relaxed ${isHero ? 'text-base' : 'text-sm'} ${isCompact ? 'line-clamp-2' : 'line-clamp-3'}`}>
          {story.excerpt}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
          <div className="flex items-center gap-4 text-white/40 text-xs font-mono">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {story.views || 0}
            </span>
            {story.tags?.slice(0, 2).map(tag => (
              <span key={tag} className="hidden md:inline">#{tag}</span>
            ))}
          </div>
          <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </Link>
  );
}
